<template>
  <admin-wrapper
    title-translation-path="admin.KOMADRES_ORDERS"
    :moduleEnabler="moduleEnabler"
    :is-form-fetched="isFormFetched"
    :inputs="inputs"
    @save="save"
  >
    <template #komadres_datasource.input="data">
      <v-col cols="3">
        <data-input v-bind="data" v-model="selectedDataSourceProps.datasource" @change="clearColumns" />
      </v-col>
    </template>
    <template v-for="(column, idx) of columns" #[`${column}.input`]="data">
      <v-col cols="3" :key="idx">
        <data-input
          v-bind="data"
          v-model="selectedDataSourceProps.settings[column]"
          @change="selectedDataSourcePropsChanged = true"
        />
      </v-col>
    </template>
    <template #additional-buttons>
      <v-col cols="auto">
        <base-button
          translation-path="button.refreshKomadresData"
          :cancel="true"
          :disabled="isRefreshButtonVisible"
          @click="handleRefreshKomadresData"
        />
      </v-col>
    </template>
  </admin-wrapper>
</template>

<script>
import { call, get } from 'vuex-pathify';

import AdminWrapper from '@/components/AdminWrapper';
import DataInput from '@/components/DataInput';

export default {
  name: 'KomadresSettings',
  components: {
    AdminWrapper,
    DataInput,
  },
  data: () => ({
    selectedDataSourceProps: {
      datasource: '',
      settings: {
        order_type_attribute: '',
        order_description_attribute: '',
      },
    },
    selectedDataSourcePropsChanged: false,
    isFormFetched: false,
    columns: ['order_type_attribute', 'order_description_attribute'],
  }),
  created() {
    if (this.isModuleEnabled) {
      this.init();
    } else {
      this.isFormFetched = true;
    }
  },
  computed: {
    dataSources: get('layers/metadata'),
    isModuleEnabled: get('admin/settingsValues@komadres_orders_module_enabled'),
    dataSourceSettingsName() {
      return 'komadres_orders_datasource';
    },
    polygonDataSources() {
      return this.$_objectToArray(this.dataSources).filter(
        dataSource => dataSource.scope === 'core' && dataSource.geometry_type === 'polygon' && !dataSource.module
      );
    },
    dataSource() {
      return this.dataSources[this.selectedDataSourceProps.datasource];
    },
    dataSourceAttributes() {
      return this.dataSource?.attributes_schema.attributes.filter(attr => attr.data_type.name === 'text') || [];
    },
    inputs() {
      const inputs = [
        {
          name: 'komadres_datasource',
          translationPath: 'admin.datasourcesTypeKomadres',
          rules: [v => !!v || v == '0' || 'rules.required'],
          dataType: {
            name: 'select',
          },
          align: 'start',
          items: this.polygonDataSources,
          itemText: 'verbose_name',
          itemValue: 'name',
        },
      ];
      for (const column of this.columns) {
        inputs.push({
          name: column,
          translationPath: `admin.${column}`,
          rules: [v => !!v || v == '0' || 'rules.required'],
          align: 'start',
          dataType: {
            name: 'select',
          },
          items: this.dataSourceAttributes,
          itemText: 'verbose_name',
          itemValue: 'name',
          disabled: !this.dataSource,
        });
      }
      return inputs;
    },
    moduleEnabler() {
      return {
        name: 'komadres_orders_module_enabled',
        translationPath: 'admin.KOMADRES_ORDERS',
        subtitle: this.$i18n.t('admin.moduleEnablerSubtitle', { suffix: this.$i18n.t('admin.KOMADRES_ORDERS') }),
      };
    },
    isRefreshButtonVisible() {
      return this.selectedDataSourceProps.datasource ? false : true;
    },
  },
  watch: {
    isModuleEnabled(nV) {
      if (nV) {
        this.isFormFetched = false;
        this.init();
      }
    },
  },
  methods: {
    editDatasourceSettings: call('datasourceSettings/editDatasourceSettings'),
    getDatasourceSettings: call('datasourceSettings/getDatasourceSettings'),
    getDataSources: call('layers/getLayersMetadata'),
    getModulesMapping: call('admin/getModulesMapping'),
    refreshKomadresData: call('externalSystems/refreshKomadresData'),
    clearColumns() {
      this.selectedDataSourceProps.settings.order_type_attribute = '';
      this.selectedDataSourceProps.settings.order_description_attribute = '';
      this.selectedDataSourcePropsChanged = true;
    },
    async init() {
      await Promise.all([this.getModulesMapping(), this.getDataSources({ with_attributes_schema: true })]);
      if (this.isModuleEnabled) {
        await this.updateSelectedDataSource();
      }
      this.isFormFetched = true;
    },
    async save() {
      if (!this.selectedDataSourcePropsChanged) {
        return;
      }
      const payload = { name: this.dataSourceSettingsName, settings: this.selectedDataSourceProps };
      try {
        await this.editDatasourceSettings(payload);
        this.selectedDataSourcePropsChanged = false;
        this.$store.set('snackbar/PUSH_SUCCESSFULLY_MESSAGE!', {
          message: this.$i18n.t('success.successEditSetting'),
        });
      } catch (error) {
        console.log('  error', error);
      }
    },
    async updateSelectedDataSource() {
      try {
        const r = await this.getDatasourceSettings(this.dataSourceSettingsName);
        const { datasource, settings } = r;
        this.selectedDataSourceProps = {
          datasource,
          settings,
        };
        this.selectedDataSourcePropsChanged = false;
      } catch {
        this.selectedDataSourceProps = {
          settings: {
            order_type_attribute: '',
            order_description_attribute: '',
          },
          datasource: '',
        };
        this.selectedDataSourcePropsChanged = false;
      }
    },
    async handleRefreshKomadresData() {
      try {
        await this.refreshKomadresData();
      } catch (error) {
        console.log('  error', error);
      }
    },
  },
};
</script>
